import {
  ReactNode,
  useState,
  useMemo,
  SetStateAction,
  Dispatch,
  useEffect,
} from 'react';
import BigNumber from 'bignumber.js';

import SETTINGS from '#/config/global-settings';

import { logException } from '#/features/logging/logging';
import {
  EthereumErc20BalanceState,
  useEthereumErc20Balance,
} from '#/features/wallet/ethereum-erc20-balance';
import { useSystemConfig } from '#/features/system/system-config-context';
import { BridgedToken } from '#/features/paraclear';

import createSafeContext from '#/utils/createSafeContext';

import fieldErrorsView from '#/views/field-errors';

import {
  DepositState,
  useDepositActions,
  useDepositState,
} from './deposit-context';
import { formatUsdcBalance } from './deposit-functions';

export interface DepositFormState {
  readonly asset: string;
  readonly amount: BigNumber | null;
}

export interface DepositFormView {
  readonly asset: AssetView;
  readonly amount: FieldView;

  // Derived
  readonly balance: string;
  readonly isValid: boolean;
  readonly isBusy: boolean;
  readonly needsAllowance: boolean;
  readonly error: string;
}

type AssetView = {
  readonly name: string;
  readonly symbol: string;
};

interface FieldView {
  /**
   * Raw value
   * @example '30000.5'
   * @example '0.34'
   */
  readonly value: string;
  readonly formattedValue: string;
  readonly isValid: boolean;
  readonly error: string;
}

export interface DepositFormActions {
  readonly updateAmount: (amount: string) => void;
  readonly reset: () => void;
}

export const INITIAL_STATE: DepositFormState = {
  asset: 'USDC',
  amount: null,
};

export const [useDepositFormState, StateCtx] =
  createSafeContext<
    [DepositFormState, Dispatch<SetStateAction<DepositFormState>>]
  >('DepositFormState');
export const [useDepositFormView, ViewCtx] =
  createSafeContext<DepositFormView>('DepositFormView');
export const [useDepositFormActions, ActionsCtx] =
  createSafeContext<DepositFormActions>('DepositFormActions');

interface ProviderProps {
  readonly children: ReactNode;
}

export function DepositFormStateProvider({ children }: ProviderProps) {
  const state = useState(INITIAL_STATE);

  return <StateCtx value={state}>{children}</StateCtx>;
}

export function DepositFormProvider({ children }: ProviderProps) {
  const [formState, setFormState] = useDepositFormState();
  const [depositState] = useDepositState();

  const config = useSystemConfig();
  const token = config.getToken(formState.asset);
  const balanceState = useEthereumErc20Balance(token);

  useAvailableAllowanceFetch(token);

  const actions = useMemo(() => createActions(setFormState), [setFormState]);
  const view = useMemo(
    () => createView(formState, depositState, balanceState),
    [formState, depositState, balanceState],
  );

  return (
    <ActionsCtx value={actions}>
      <ViewCtx value={view}>{children}</ViewCtx>
    </ActionsCtx>
  );
}

export function createView(
  formState: DepositFormState,
  depositState: DepositState,
  balanceState: EthereumErc20BalanceState,
): DepositFormView {
  const asset = { name: 'USD Coin', symbol: 'USDC' };
  const amount = amountView(formState, balanceState);
  const balance = balanceView(balanceState);
  const needsAllowance =
    amount.isValid && needsAllowanceView(formState, depositState);
  const isBusy = isBusyView(depositState);

  const isValid = !needsAllowance && [amount].every((field) => field.isValid);
  const error = fieldErrorsView([amount]);

  return {
    asset,
    amount,
    balance,
    error,
    needsAllowance,
    isBusy,
    isValid,
  };
}

function amountView(
  state: DepositFormState,
  balanceState: EthereumErc20BalanceState,
): DepositFormView['amount'] {
  const { amount } = state;

  if (amount == null) {
    return {
      value: '',
      formattedValue: '',
      isValid: false,
      error: '',
    };
  }

  const value = amount.toString();
  const formattedValue = `${formatUsdcBalance(amount)} USDC`;

  if (balanceState.status === 'error') {
    // Do not block user from submitting form when there's
    // an error but let them know that something went wrong.
    return {
      value,
      formattedValue,
      isValid: true,
      error: balanceState.error,
    };
  }

  if (balanceState.value == null) {
    return {
      value,
      formattedValue,
      isValid: false,
      error: '',
    };
  }

  if (balanceState.value.isEqualTo(0)) {
    return {
      value,
      formattedValue,
      isValid: false,
      error:
        'You have no balance in your Ethereum wallet. ' +
        `Deposit some ${state.asset} and try again.`,
    };
  }

  if (amount.isGreaterThan(balanceState.value)) {
    return {
      value,
      formattedValue,
      isValid: false,
      error: "It's not possible to deposit more than your current balance.",
    };
  }

  return {
    value,
    formattedValue,
    isValid: amount.isGreaterThan(0),
    error: '',
  };
}

function balanceView(
  balanceState: EthereumErc20BalanceState,
): DepositFormView['balance'] {
  if (balanceState.value == null) return '-';
  return balanceState.value.toFormat(
    SETTINGS.tokens.USDC.scale,
    BigNumber.ROUND_DOWN,
  );
}

function needsAllowanceView(
  formState: DepositFormState,
  depositState: DepositState,
): DepositFormView['needsAllowance'] {
  if (formState.amount == null) {
    return false;
  }
  return depositState.allowance.isLessThan(formState.amount);
}

function isBusyView(depositState: DepositState): DepositFormView['isBusy'] {
  return depositState.status !== 'idle';
}

export function createActions(
  setState: Dispatch<SetStateAction<DepositFormState>>,
): DepositFormActions {
  const updateAmount = (amount: string) => {
    const parsed = new BigNumber(amount);
    setState((state) => ({
      ...state,
      amount: parsed.isFinite() ? parsed : null,
    }));
  };
  const reset = () => {
    setState(() => INITIAL_STATE);
  };

  return {
    updateAmount,
    reset,
  };
}

function useAvailableAllowanceFetch(token: BridgedToken) {
  const { fetchEthereumBridgeAllowance } = useDepositActions();

  useEffect(() => {
    fetchEthereumBridgeAllowance(token).catch((err) => {
      const message = 'Unexpected error in useAvailableAllowanceFetch';
      logException(new Error(message, { cause: err }));
    });
    // This effect should only run once.
    // Changes in fetchEthereumBridgeAllowance should not make it run again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
